import * as CheckboxPrimitive from '@radix-ui/react-checkbox'
import { type VariantProps, cva } from 'class-variance-authority'
import { Check, Minus } from 'lucide-react'
import * as React from 'react'

import { cn } from '@/lib/ui/style-helpers'

const checkboxVariants = cva(
  'peer flex size-4 shrink-0 items-center justify-center rounded transition-colors focus-visible:ring-accent-600 focus-visible:ring-2 focus-visible:ring-offset-2',
  {
    variants: {
      variant: {
        default:
          'border border-main-border-semiBold bg-main-background-card data-[state=checked]:border-transparent data-[state=checked]:bg-main-background-interactive-input-default data-[state=checked]:text-white',
        ai: 'border border-main-border-semiBold bg-main-background-card data-[state=checked]:border-transparent data-[state=checked]:bg-main-background-ai data-[state=checked]:text-white',
      },
      disabled: {
        true: 'cursor-not-allowed data-[state=unchecked]:border-main-border-disabled data-[state=unchecked]:bg-main-background-interactive-input-disabled data-[state=checked]:bg-main-background-interactive-input-disabled data-[state=indeterminate]:bg-main-background-interactive-input-disabled',
        false: 'hover:border-main-border-focus hover:bg-main-background-interactive-input-hover',
      },
      error: {
        true: 'border-main-border-error',
      },
    },
    defaultVariants: {
      variant: 'default',
      disabled: false,
      error: false,
    },
  },
)

export interface CheckboxProps
  extends
    Omit<React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>, 'disabled'>,
    VariantProps<typeof checkboxVariants> {
  indeterminate?: boolean
  disabled?: boolean
  error?: boolean
}
const Checkbox = React.forwardRef<React.ElementRef<typeof CheckboxPrimitive.Root>, CheckboxProps>(
  ({ className, indeterminate, disabled, error, variant, ...props }, ref) => (
    <CheckboxPrimitive.Root
      ref={ref}
      className={cn(checkboxVariants({ variant, disabled, error }), className)}
      data-indeterminate={indeterminate ? '' : undefined}
      disabled={disabled}
      aria-checked={
        indeterminate
          ? 'mixed'
          : props.checked === 'indeterminate'
            ? 'mixed'
            : typeof props.checked === 'boolean'
              ? props.checked
              : undefined
      }
      {...props}
    >
      <CheckboxPrimitive.Indicator
        className={cn('flex size-4 items-center justify-center text-main-foreground-white', {
          'text-main-foreground-disabled': disabled,
        })}
      >
        {indeterminate ? (
          <Minus strokeWidth={3} className='size-3' />
        ) : (
          <Check strokeWidth={3} className='size-3' />
        )}
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  ),
)
Checkbox.displayName = CheckboxPrimitive.Root.displayName

export interface CheckboxWithLabelProps extends CheckboxProps {
  children?: React.ReactNode
  description?: React.ReactNode
  labelClassName?: string
  truncateLabel?: boolean
}

const CheckboxWithLabel = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  CheckboxWithLabelProps
>(
  (
    {
      children,
      description,
      className,
      labelClassName,
      truncateLabel = true,
      disabled,
      variant,
      ...props
    },
    ref,
  ) => {
    const title =
      truncateLabel && (typeof children === 'string' || typeof children === 'number')
        ? String(children)
        : undefined

    const checkbox = (
      <Checkbox ref={ref} className={className} disabled={disabled} variant={variant} {...props} />
    )
    const label = (
      <span
        title={title}
        className={cn(
          'text-main-foreground-primary',
          truncateLabel ? 'max-w-80 truncate' : 'break-words',
          labelClassName,
        )}
      >
        {children}
      </span>
    )

    return (
      <label
        className={cn(
          'flex cursor-pointer select-none gap-1.5',
          description ? 'items-start' : 'items-center',
          disabled && 'cursor-not-allowed',
        )}
      >
        {description ? <span className='flex h-5 items-center'>{checkbox}</span> : checkbox}
        {description ? (
          <span className='flex min-w-0 flex-col'>
            {label}
            <span className='text-main-foreground-secondary text-body-small'>{description}</span>
          </span>
        ) : (
          label
        )}
      </label>
    )
  },
)
CheckboxWithLabel.displayName = 'CheckboxWithLabel'

export { Checkbox, CheckboxWithLabel }
